import { Injectable } from '@angular/core';
import { MINUTES_TO_MILLISECONDS } from '@api-ui-app/src/app/lib/constants';
import { getSelectedClientId } from '@api-ui-app/src/app/ngrx/auth.reducer';
import { getSelectedStoreId } from '@api-ui-app/src/app/ngrx/e-commerce-stores.selector';
import { getGelatoPlusGoldIsActive } from '@api-ui-app/src/app/subscriptions/+state/subscriptions.selectors';
import { logEvent } from '@gelato-api-ui/core/analytics/helpers/trackEvent';
import { callWithRetries } from '@gelato-api-ui/core/api/helpers/callWithRetries';
import { EditorInboundEventType } from '@gelato-api-ui/core/design-editor/types/editor-inbound-event-type.enum';
import { Asset } from '@gelato-api-ui/core/designs/asset';
import { AssetCreateRequest } from '@gelato-api-ui/core/designs/asset-api';
import { AssetImportFromShutterstockRequest } from '@gelato-api-ui/core/designs/asset-import-from-shutterstock-request';
import { AssetProviderUid } from '@gelato-api-ui/core/designs/asset-provider-uid.enum';
import { AssetStatus } from '@gelato-api-ui/core/designs/asset-status.enum';
import { AssetsApiService } from '@gelato-api-ui/core/designs/services/assets-api.service';
import { UploadedImageDataService } from '@gelato-api-ui/core/designs/services/uploaded-image-data.service';
import { UploadedImageData } from '@gelato-api-ui/core/designs/uploaded-image-data';
import { MimeType } from '@gelato-api-ui/core/preflight/mime-type.enum';
import { PreflightGraphQlService } from '@gelato-api-ui/core/preflight/services/preflight-graph-ql.service';
import { ShutterstockImage } from '@gelato-api-ui/core/shutterstock/shutterstock-image';
import { DesignEditorCommunicationService } from '@gelato-api-ui/design-editor/services/design-editor-communication.service';
import { ShowNotification } from '@gelato-api-ui/ui-kit/src/lib/notification/notification.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, delay, filter, first, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as actions from './assets.actions';
import { DesignEditorSharedService } from '@gelato-api-ui/design-editor/services/design-editor-shared.service';

@Injectable()
export class AssetsEffects {
  private readonly selectedClientId$ = this.store$.select(getSelectedClientId);
  private readonly selectedStoreId$ = this.store$.select(getSelectedStoreId);
  private readonly getGelatoPlusGoldIsActive$ = this.store$.select(getGelatoPlusGoldIsActive);
  private readonly assetsCheckStatusDurationTime: Record<string, number> = {};
  private readonly assetCheckStatusRetryDelay = 10000;

  ProcessFileAddedPayload$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<actions.ProcessFileAddedPayload>(actions.AssetsActionTypes.ProcessFileAddedPayload),
        tap(action => {
          this.processFileAddedPayload(action.uppyPayload);
        }),
      ),
    { dispatch: false },
  );

  GetAsset$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<actions.GetAsset>(actions.AssetsActionTypes.GetAsset),
        tap(action => this.getAsset(action.assetId).subscribe()),
      ),
    { dispatch: false },
  );

  RemoveAsset$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<actions.RemoveAsset>(actions.AssetsActionTypes.RemoveAsset),
        tap(action => {
          this.removeAsset(action.assetId);
        }),
      ),
    { dispatch: false },
  );

  ProcessUploadSuccessPayload$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<actions.ProcessUploadSuccessPayload>(actions.AssetsActionTypes.ProcessUploadSuccessPayload),
        withLatestFrom(this.selectedClientId$, this.selectedStoreId$, this.getGelatoPlusGoldIsActive$),
        tap(([action, selectedClientId, selectedStoreId, isGelatoPlusGoldActive]) => {
          this.processUploadSuccessPayload(
            action.uppyPayload,
            selectedClientId,
            selectedStoreId,
            isGelatoPlusGoldActive,
          )
            .pipe(first())
            .subscribe();
        }),
      ),
    { dispatch: false },
  );

  ProcessUploadErrorPayload$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<actions.ProcessUploadErrorPayload>(actions.AssetsActionTypes.ProcessUploadErrorPayload),
        tap(action => {
          this.processUploadErrorPayload(action.uppyPayload);
        }),
      ),
    { dispatch: false },
  );

  ImportImageFromShutterstock$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<actions.ImportImageFromShutterstock>(actions.AssetsActionTypes.ImportImageFromShutterstock),
        withLatestFrom(this.selectedClientId$, this.selectedStoreId$, this.getGelatoPlusGoldIsActive$),
        tap(([action, selectedClientId, selectedStoreId, isGelatoPlusGoldActive]) => {
          this.importImageFromShutterstock(
            action.assetProviderUid,
            action.image,
            selectedClientId,
            selectedStoreId,
            isGelatoPlusGoldActive,
          )
            .pipe(first())
            .subscribe();
        }),
      ),
    { dispatch: false },
  );

  quantiseAsset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.quantizeAsset),
      switchMap(({ assetId }) =>
        this.assetsApiService.quantizeAsset(assetId).pipe(
          map(asset => actions.quantizeAssetSuccess({ asset })),
          catchError(e => of(actions.quantizeAssetError({ assetId, error: e.message }))),
        ),
      ),
    ),
  );

  quantiseAssetSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.quantizeAssetSuccess),
        switchMap(({ asset }) => this.designEditorSharedService.mapAssetsToUploadedImageData([asset], false)),
        tap(assets => {
          this.designEditorCommunicationService.sendPostMessage(EditorInboundEventType.setQuantizedAsset, assets[0]);
        }),
      ),
    { dispatch: false },
  );

  quantiseAssetError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.quantizeAssetError),
        tap(({ assetId, error }) => {
          this.designEditorCommunicationService.sendPostMessage(EditorInboundEventType.setQuantizedAssetError, {
            assetId,
            error,
          });
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store$: Store,
    private readonly translateService: TranslateService,
    private readonly designEditorCommunicationService: DesignEditorCommunicationService,
    private readonly assetsApiService: AssetsApiService,
    private readonly preflightGraphQlService: PreflightGraphQlService,
    private readonly uploadedImageDataService: UploadedImageDataService,
    public readonly designEditorSharedService: DesignEditorSharedService,
  ) {}

  processFileAddedPayload(uppyPayload: any): void {
    if (uppyPayload.type !== MimeType.PDF) {
      return;
    }

    const uppyId = uppyPayload.id;

    this.designEditorCommunicationService.sendPostMessage(EditorInboundEventType.previewGeneratedSuccess, {
      ...uppyPayload,
      id: uppyId,
      placeholder: true,
      previewData: [],
      preview: null,
    });
  }

  validateUploadSuccessPayload(uppyPayload: any): boolean {
    if (!uppyPayload || !uppyPayload.file) {
      Sentry.captureException(new Error('AssetSavingFailed'), scope => {
        scope.setExtras({
          error: 'Empty or incomplete uppyPayload',
          uppyPayload,
        });

        return scope;
      });

      return false;
    }

    return true;
  }

  processUploadSuccessPayload(
    uppyPayload: any,
    selectedClientId: string,
    selectedStoreId: string,
    isGelatoPlusGoldActive: boolean,
  ): Observable<Asset | Asset[]> {
    if (!this.validateUploadSuccessPayload(uppyPayload)) {
      return of(null);
    }

    if (uppyPayload.file.type === MimeType.PDF) {
      return this.saveUploadedPdfFile(selectedClientId, selectedStoreId, uppyPayload).pipe(
        catchError(() => of(null)),
        tap((savedAssets: Asset[]) => {
          this.processSavedPdfAssets(uppyPayload, savedAssets);
        }),
      );
    }

    return this.saveUploadedFile(selectedClientId, selectedStoreId, uppyPayload).pipe(
      tap((savedAsset: Asset) => {
        this.processSavedAsset(uppyPayload, savedAsset, isGelatoPlusGoldActive);
      }),
    );
  }

  processUploadErrorPayload(uppyPayload: any): void {
    Sentry.captureException(new Error('UploadFailed'), scope => {
      scope.setExtras({ uppyPayload });

      return scope;
    });

    this.designEditorCommunicationService.sendPostMessage(EditorInboundEventType.uploadError, uppyPayload);
  }

  processSavedAsset(uppyPayload: any, savedAsset: Asset, isGelatoPlusGoldActive: boolean): void {
    Sentry.addBreadcrumb({
      category: 'AssetsEffects',
      message: 'ProcessSavedAsset',
      level: Sentry.Severity.Info,
    });

    if (!uppyPayload || !uppyPayload.file) {
      Sentry.captureException(new Error('EmptyOrIncompleteUppyPayloadPassed'), scope => {
        scope.setExtras({ uppyPayload });

        return scope;
      });

      return;
    }

    const uppyId = uppyPayload.file.id;

    if (!uppyId) {
      Sentry.captureException(new Error('EmptyUppyIdPassed'), scope => {
        scope.setExtras({ uppyPayload });

        return scope;
      });

      return;
    }

    if (!savedAsset) {
      Sentry.captureException(new Error('EmptyAssetPassed'), scope => {
        scope.setExtras({ uppyId });

        return scope;
      });

      return;
    }

    this.mapAsset(savedAsset);

    const uploadedAssetData: UploadedImageData = this.uploadedImageDataService.getUploadedImageDataFromAsset(
      uppyId,
      savedAsset,
      isGelatoPlusGoldActive,
    );

    this.designEditorCommunicationService.sendPostMessage(EditorInboundEventType.uploadSuccess, uploadedAssetData);
  }

  processSavedPdfAssets(originalUppyPayload: any, savedAssets: Asset[]): void {
    const uppyId = originalUppyPayload.file.id;

    this.designEditorCommunicationService.sendPostMessage(EditorInboundEventType.removeImages, [uppyId]);

    if (!savedAssets || !savedAssets.length) {
      return;
    }

    const uploadedImagesData: UploadedImageData[] = savedAssets.map((loopAsset: Asset) =>
      this.uploadedImageDataService.getUploadedImageDataFromAsset(loopAsset.id, loopAsset),
    );

    this.designEditorCommunicationService.sendPostMessage(
      EditorInboundEventType.loadUploadedImages,
      uploadedImagesData,
    );
  }

  removeAsset(assetId: string): Observable<Asset> {
    return callWithRetries(() => this.assetsApiService.delete(assetId)).pipe(
      catchError(error => {
        Sentry.captureException(new Error('AssetDeletionFailed'), scope => {
          scope.setExtras({
            id: assetId,
            error,
          });

          return scope;
        });

        return of(null);
      }),
      map((deletedAsset: Asset) => {
        if (!deletedAsset) {
          return null;
        }

        Sentry.addBreadcrumb({
          category: 'AssetsEffects',
          message: 'AssetDeletionSuccess',
          data: {
            id: assetId,
            response: deletedAsset,
          },
          level: Sentry.Severity.Info,
        });

        return deletedAsset;
      }),
    );
  }

  getAsset(assetId: string): Observable<Asset> {
    return callWithRetries(() => this.assetsApiService.getOne(assetId)).pipe(
      filter(asset => Boolean(asset)),
      map(asset => {
        this.store$.dispatch(new actions.SetAsset(asset));

        return asset;
      }),
      delay(this.assetCheckStatusRetryDelay),
      tap(asset => {
        if (asset.status === AssetStatus.PENDING) {
          // store the time of the start retrying
          this.assetsCheckStatusDurationTime[assetId] =
            this.assetsCheckStatusDurationTime[assetId] || new Date().getTime() + 5 * MINUTES_TO_MILLISECONDS;

          // check if retrying is still ongoing
          if (new Date().getTime() < this.assetsCheckStatusDurationTime[assetId]) {
            this.store$.dispatch(new actions.GetAsset(asset.id));
          } else {
            this.assetsCheckStatusDurationTime[assetId] = null;
          }
        }
      }),
    );
  }

  saveUploadedFile(selectedClientId: string, selectedStoreId: string, uppyPayload: any): Observable<Asset> {
    const uppyId = uppyPayload.file.id;

    const assetCreateRequest: AssetCreateRequest = {
      clientId: selectedClientId,
      storeId: selectedStoreId,
      fileUrl: uppyPayload.url,
    };

    return callWithRetries(() => this.assetsApiService.create(assetCreateRequest)).pipe(
      catchError(error => {
        Sentry.captureException(new Error('AssetSavingFailed'), scope => {
          scope.setExtras({
            uppyId,
            request: assetCreateRequest,
            error,
          });

          return scope;
        });

        this.store$.dispatch(new actions.ProcessUploadErrorPayload(uppyPayload));

        return of(null);
      }),
      map((savedAsset: Asset) => {
        if (!savedAsset) {
          return null;
        }

        if (savedAsset.status === AssetStatus.FAILED) {
          this.store$.dispatch(
            new ShowNotification({
              message: this.translateService.instant('txt_file_upload_validation_error_message'),
              type: 'error',
            }),
          );
        }

        Sentry.addBreadcrumb({
          category: 'AssetsEffects',
          message: 'AssetSavingSuccess',
          data: {
            uppyId,
            request: assetCreateRequest,
            response: savedAsset,
          },
          level: Sentry.Severity.Info,
        });

        return savedAsset;
      }),
    );
  }

  saveUploadedPdfFile(selectedClientId: string, selectedStoreId: string, uppyPayload: any): Observable<Asset[]> {
    const uppyId = uppyPayload.file.id;

    return this.preflightGraphQlService.splitPdf(uppyPayload.url).pipe(
      switchMap((response: string[]): Observable<Asset[]> => {
        if (!response) {
          return of(null);
        }

        const observables: Observable<Asset>[] = response.map(
          (pdfPageUrl: string, index: number): Observable<Asset> => {
            const pageUppyId = `${uppyId}-page-${index + 1}`;
            const pageUppyPayload = {
              file: { id: pageUppyId, type: MimeType.PDF },
              url: pdfPageUrl,
            };

            return this.saveUploadedFile(selectedClientId, selectedStoreId, pageUppyPayload).pipe(
              tap((savedAsset: Asset) => {
                if (savedAsset?.status === AssetStatus.PENDING) {
                  setTimeout(
                    () => this.store$.dispatch(new actions.GetAsset(savedAsset.id)),
                    this.assetCheckStatusRetryDelay,
                  );
                }

                this.mapAsset(savedAsset);
              }),
            );
          },
        );

        return forkJoin(observables);
      }),
    );
  }

  importImageFromShutterstock(
    assetProviderUid: AssetProviderUid,
    image: ShutterstockImage,
    selectedClientId: string,
    selectedStoreId: string,
    isGelatoPlusGoldActive: boolean,
  ): Observable<Asset> {
    logEvent('shutterstockImageAddToArtboard', {
      shutterstockContentId: image.id,
    });
    logEvent('Editor Image Added', {
      assetProviderUid,
    });

    const uppyId = `uppy-shutterstock/${image.id}-${new Date().getTime()}`;

    this.designEditorCommunicationService.sendPostMessage(EditorInboundEventType.previewGeneratedRemotelySuccess, {
      id: uppyId,
      isRemote: true,
      preview: image.url,
      source: 'Shutterstock',
      width: image.width,
      height: image.height,
    });

    let fakeProgress = 0;
    const fakeProgressTimer = setInterval(() => {
      fakeProgress = Math.min(100, fakeProgress + 20);

      this.designEditorCommunicationService.sendPostMessage(EditorInboundEventType.uploadProgress, {
        file: { id: uppyId, width: image.width, height: image.height, isRemote: true },
        bytesTotal: 100,
        bytesUploaded: fakeProgress,
      });
    }, 1000);

    const assetImportFromShutterstockRequest: AssetImportFromShutterstockRequest = {
      clientId: selectedClientId,
      storeId: selectedStoreId,
      assetProviderUid,
      providerAssetId: image.id,
      searchId: image.search_id,
    };

    return callWithRetries(() => this.assetsApiService.importFromShutterstock(assetImportFromShutterstockRequest)).pipe(
      catchError(error => {
        Sentry.captureException(new Error('AssetImportFromShutterstockFailed'), scope => {
          scope.setExtras({
            request: assetImportFromShutterstockRequest,
            error,
          });

          return scope;
        });

        this.store$.dispatch(new actions.ProcessUploadErrorPayload({ file: { id: uppyId }, url: image.url }));
        this.designEditorCommunicationService.sendPostMessage(EditorInboundEventType.removeImages, [uppyId]);

        return of(null);
      }),
      map((savedAsset: Asset) => {
        if (!savedAsset) {
          return null;
        }

        Sentry.addBreadcrumb({
          category: 'AssetsEffects',
          message: 'AssetSavingSuccess',
          data: {
            uppyId,
            request: assetImportFromShutterstockRequest,
            response: savedAsset,
          },
          level: Sentry.Severity.Info,
        });

        return savedAsset;
      }),
      tap((savedAsset: Asset) => {
        clearInterval(fakeProgressTimer);
        const uppyPayloadForSavedAsset = { file: { id: uppyId } };
        this.processSavedAsset(uppyPayloadForSavedAsset, savedAsset, isGelatoPlusGoldActive);
      }),
    );
  }

  private mapAsset(savedAsset: Asset) {
    this.store$.dispatch(new actions.SetAsset(savedAsset));
  }
}
